import { createFileRoute, Link } from '@tanstack/react-router';
import { CodeBlock, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';
import tutorialStyles from './Tutorial.module.css';

export const Route = createFileRoute('/documentation/tutorial')({
  component: TutorialPage,
});

function TutorialPage() {
  return (
    <div className={tutorialStyles.page}>
      <div className={styles.breadcrumb}>Getting Started</div>
      <h1 className={styles.pageTitle}>Stream Your First Application</h1>
      <p className={styles.pageSubtitle}>
        Get a GPU-rendered Unreal Engine application streaming to your browser
        in under five minutes.
      </p>

      <div className={tutorialStyles.badge}>
        <span>~5 min</span>
        <span className={tutorialStyles.badgeSep} />
        <span>3 steps</span>
      </div>

      {/* ── Video placeholder ── */}
      <div className={tutorialStyles.videoPlaceholder}>
        <div className={tutorialStyles.videoPlayIcon}>
          <div className={tutorialStyles.videoPlayTriangle} />
        </div>
        <span className={tutorialStyles.videoLabel}>
          Watch the full walkthrough (3:30)
        </span>
      </div>

      {/* ── Prerequisites ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="prerequisites">
          Before You Begin
        </h2>
        <ul className={tutorialStyles.prereqList}>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              An <span className={styles.emphasis}>Interlucent account</span>{' '}
              with at least one uploaded application
            </span>
          </li>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              An{' '}
              <span className={styles.emphasis}>admission token</span> for that
              application — generate one from your project settings or
              via the{' '}
              <Link to="/documentation/sdk-reference">SDK</Link>
            </span>
          </li>
          <li className={tutorialStyles.prereqItem}>
            <span className={tutorialStyles.prereqCheck} />
            <span>
              A text editor and a modern browser (Chrome, Firefox, Safari 15.4+,
              or Edge)
            </span>
          </li>
        </ul>
        <InfoCallout variant="tip">
          You can generate a test token from{' '}
          <strong>Console &gt; Project Settings &gt; Admission Tokens</strong>.
          For production, create tokens server-side using the SDK — see
          the <Link to="/documentation/sdk-reference">SDK Reference</Link>.
        </InfoCallout>
      </section>

      {/* ── Step 1 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="create-your-page">
          Step 1 — Create Your Page
        </h2>
        <p className={styles.prose}>
          Create a folder and add an <code>index.html</code> file with this
          boilerplate. The two CSS rules give you a full-viewport, black canvas
          for the stream.
        </p>
        <CodeBlock language="html" title="index.html">
{`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>My First Stream</title>
  <style>
    body { margin: 0; background: #000; }
    pixel-stream { display: block; width: 100vw; height: 100vh; }
  </style>
</head>
<body>

</body>
</html>`}
        </CodeBlock>
      </section>

      {/* ── Step 2 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="add-the-stream">
          Step 2 — Add the Stream
        </h2>
        <p className={styles.prose}>
          Add three things inside the <code>&lt;body&gt;</code> tag: the{' '}
          <code>&lt;pixel-stream&gt;</code> element, the CDN script, and a
          short script that sets your admission token. Here is the complete
          file:
        </p>
        <CodeBlock language="html" title="index.html">
{`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>My First Stream</title>
  <style>
    body { margin: 0; background: #000; }
    pixel-stream { display: block; width: 100vw; height: 100vh; }
  </style>
</head>
<body>
  <pixel-stream></pixel-stream>

  <script src="https://cdn.interlucent.ai/dev/pixel-stream/0.0.75/pixel-stream.iife.min.js"></script>
  <script>
    const ps = document.querySelector('pixel-stream');
    ps.admissionToken = 'YOUR_TOKEN';
  </script>
</body>
</html>`}
        </CodeBlock>
        <p className={styles.prose}>
          That is the entire integration. One HTML element, one script tag,
          one line of JavaScript. The{' '}
          <code>controls</code> attribute enables the built-in overlay — a play
          button when idle, a spinner during connection, and error handling with
          retry. Replace <code>YOUR_TOKEN</code> with your admission token.
        </p>
        <InfoCallout variant="info">
          The <code>controls</code> attribute enables the component's built-in
          overlay: a play button when idle, a spinner during connection, and an
          error state with retry. Remove it when you are ready to build your own
          UI with the <code>slot="overlay"</code> pattern.
        </InfoCallout>
        <InfoCallout variant="tip">
          You can also install the package via npm —{' '}
          <code>npm install @interlucent/pixel-stream</code> — and import it as
          an ES module. The CDN approach is shown here because it requires zero
          tooling.
        </InfoCallout>
      </section>

      {/* ── Step 3 ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="open-and-play">
          Step 3 — Open and Play
        </h2>
        <p className={styles.prose}>
          Open <code>index.html</code> in your browser. You can double-click
          the file directly or serve it with a simple HTTP server:
        </p>
        <CodeBlock language="bash" title="Terminal">
{`# Node.js (npx, no install needed)
npx serve .

# Python 3
python3 -m http.server 8000`}
        </CodeBlock>
        <p className={styles.prose}>
          You will see a dark viewport with a{' '}
          <span className={styles.emphasis}>play button</span> in the center.
          Click it. The overlay transitions to a spinner as the component
          connects to the platform, authenticates your token, requests a GPU
          worker, and negotiates the WebRTC connection. Within a few seconds,
          a live stream fills the viewport.
        </p>
        <p className={styles.prose}>
          Move your mouse. Click. Press keyboard keys. Input flows back to the
          application automatically. That's it — you're streaming.
        </p>
        <InfoCallout variant="warning">
          If the play button never appears, open your browser's developer
          console (F12) and check for script loading errors. If the spinner
          stays indefinitely, your admission token may have expired or your
          project may not have available GPU capacity. Generate a fresh token
          and try again.
        </InfoCallout>
      </section>

      {/* ── Make It Yours ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="make-it-yours">
          Make It Yours
        </h2>
        <p className={styles.prose}>
          You have a working stream. Here are three patterns that take it
          further — each just a few lines of code.
        </p>

        <h3 className={styles.subsectionTitle} id="custom-overlay">
          Custom Overlay
        </h3>
        <p className={styles.prose}>
          Replace the built-in controls with your own branded UI using the{' '}
          <code>overlay</code> slot. The component sets a{' '}
          <code>status</code> attribute on itself (<code>idle</code>,{' '}
          <code>queued</code>, <code>streaming</code>, <code>failed</code>,
          etc.) so you can show and hide states with pure CSS.
        </p>
        <CodeBlock language="html" title="custom-overlay.html">
{`<pixel-stream id="ps">
  <div slot="overlay" style="display:grid; place-items:center; height:100%; color:#fff;">
    <button onclick="document.getElementById('ps').play()">
      Launch Experience
    </button>
  </div>
</pixel-stream>`}
        </CodeBlock>
        <p className={styles.prose}>
          See the{' '}
          <Link to="/documentation/streaming-guide" hash="custom-overlay">
            Streaming Guide
          </Link>{' '}
          for the full overlay pattern with CSS status selectors.
        </p>

        <h3 className={styles.subsectionTitle} id="listen-for-events">
          Listen for Events
        </h3>
        <p className={styles.prose}>
          React to the session lifecycle in JavaScript:
        </p>
        <CodeBlock language="javascript" title="events.js">
{`const ps = document.querySelector('pixel-stream');

ps.addEventListener('status-change', (e) => {
  console.log('Status:', e.detail.newStatus);
});

ps.addEventListener('data-channel-open', () => {
  console.log('Data channel ready — you can now send messages');
});`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="send-a-message">
          Send a Message to the Game
        </h3>
        <p className={styles.prose}>
          Once the data channel is open, send structured JSON to the running
          application:
        </p>
        <CodeBlock language="javascript" title="send-message.js">
{`ps.addEventListener('data-channel-open', () => {
  ps.sendUIInteraction({
    action: 'setColor',
    target: 'car-body',
    color: '#FF5500'
  });
});`}
        </CodeBlock>
        <InfoCallout variant="warning">
          Always wait for the <code>data-channel-open</code> event before
          sending messages. Calls made before the data channel is ready will be
          silently dropped.
        </InfoCallout>
      </section>

      {/* ── Next Steps ── */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="next-steps">
          Next Steps
        </h2>
        <p className={styles.prose}>
          You have a stream running and you know the building blocks. Here is
          where to go deeper:
        </p>
        <div className={styles.linkGrid}>
          <Link
            to="/documentation/streaming-guide"
            className={styles.linkCard}
          >
            <div className={styles.linkCardTitle}>Streaming Guide</div>
            <div className={styles.linkCardDesc}>
              The complete, progressive walkthrough — configuration, custom
              overlays, bidirectional messaging, and a full configurator
              example.
            </div>
          </Link>
          <Link
            to="/documentation/sdk-reference"
            className={styles.linkCard}
          >
            <div className={styles.linkCardTitle}>SDK Reference</div>
            <div className={styles.linkCardDesc}>
              Generate admission tokens server-side. Covers minimal,
              production, low-latency, and resilient token patterns.
            </div>
          </Link>
          <Link
            to="/documentation/tolerances"
            className={styles.linkCard}
          >
            <div className={styles.linkCardTitle}>Session Behavior</div>
            <div className={styles.linkCardDesc}>
              Configure timing constraints for queue wait, reconnection grace
              periods, and WebRTC negotiation windows.
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
